/**
 * AiHelpPanel - Pannello della chat con l'assistente AI.
 *
 * Stile Flusia (grigi e nero, niente rosa): intestazione con nuova chat, cronologia, allarga e chiudi;
 * all'apertura saluto e domande legate alla pagina; tuoi messaggi in fumetto grigio, risposte come testo
 * con "Copia". I link interni del CRM navigano con useNavigate() senza ricaricare la pagina.
 */

import { useRef, useEffect, useState } from 'react';
import { X, Loader2, Maximize2, Minimize2, Plus, History, Sparkles, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDynamicHeaderSafe } from '../../contexts/DynamicHeaderContext';
import { useAiHelpStore } from '../../lib/stores/aiHelpStore';
import { useAuth } from '../../lib/auth';
import { AiHelpMessage, AiHelpThinking } from './AiHelpMessage';
import { AiHelpComposer } from './AiHelpComposer';
import { suggestionsFor, greeting } from './aiHelpSuggestions';

interface AiHelpPanelProps {
  onClose: () => void;
  onBack: () => void;
}

const iconBtn = 'p-1.5 rounded-md text-[#787774] hover:bg-[rgba(55,53,47,0.08)] hover:text-[#37352F] dark:hover:text-white transition-colors';

export function AiHelpPanel({ onClose, onBack }: AiHelpPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const headerContext = useDynamicHeaderSafe();
  const { userProfile } = useAuth();
  const { messages, loadingMessages, streaming, streamingContent, currentSessionId, sessions, sendMessage, rateLimitError, startNewChat } = useAiHelpStore();
  const [expanded, setExpanded] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const currentSession = sessions.find((s) => s.id === currentSessionId);
  const path = location.pathname + location.search;
  const { area, questions } = suggestionsFor(path);
  // Sotto l'intestazione del CRM (anche nelle impostazioni). z-[56]: sopra barra filtri (z-50) e drawer delle
  // schede (z-[55]), sotto drawer secondari e modali (z-[60]).
  const top = 'top-12';

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length, streamingContent]);

  const pageContext = () => ({ pathname: path, sectionId: headerContext?.config?.sectionId, title: headerContext?.config?.title });
  const ask = (text: string) => { if (!streaming) sendMessage(text, pageContext()); };
  const openInternal = (href: string) => { navigate(href); };
  const empty = messages.length === 0 && !streaming;

  return (
    <aside
      aria-label="Assistente AI"
      className={`fixed ${top} right-0 bottom-0 z-[56] bg-white dark:bg-gray-900 flex flex-col overflow-hidden border-l border-[rgba(55,53,47,0.09)] dark:border-gray-700 shadow-[-4px_0_16px_rgba(0,0,0,0.06)] animate-[slideInRight_0.25s_ease-out] max-w-full`}
      style={{ width: expanded ? 640 : 420 }}
    >
      <header className="flex items-center gap-2 px-3 h-12 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 flex-shrink-0">
        <Sparkles className="w-4 h-4 text-[#787774] flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#37352F] dark:text-white leading-tight">Assistente</p>
          {currentSession?.title && <p className="text-[11px] text-[#9B9A97] truncate leading-tight">{currentSession.title}</p>}
        </div>
        <button type="button" className={iconBtn} onClick={() => startNewChat()} title="Nuova chat" aria-label="Nuova chat"><Plus className="w-4 h-4" strokeWidth={1.75} /></button>
        <button type="button" className={iconBtn} onClick={onBack} title="Conversazioni precedenti" aria-label="Conversazioni precedenti"><History className="w-4 h-4" strokeWidth={1.75} /></button>
        <button type="button" className={`${iconBtn} hidden sm:block`} onClick={() => setExpanded((e) => !e)} title={expanded ? 'Riduci' : 'Allarga'} aria-label={expanded ? 'Riduci' : 'Allarga'}>
          {expanded ? <Minimize2 className="w-4 h-4" strokeWidth={1.75} /> : <Maximize2 className="w-4 h-4" strokeWidth={1.75} />}
        </button>
        <button type="button" className={iconBtn} onClick={onClose} title="Chiudi" aria-label="Chiudi"><X className="w-4 h-4" strokeWidth={1.75} /></button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loadingMessages ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-[#9B9A97]" /></div>
        ) : empty ? (
          <div className="flex flex-col justify-end h-full gap-5 pb-2">
            <div>
              <p className="text-lg font-semibold text-[#37352F] dark:text-white">{greeting(userProfile?.full_name)}</p>
              <p className="text-sm text-[#787774] mt-1">Chiedimi come si fa qualcosa in Flusia: ti spiego i passi e ti porto nella pagina giusta.</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#9B9A97] mb-1.5">{area ? `Su ${area}` : 'Domande frequenti'}</p>
              <ul className="-mx-2">
                {questions.map((q) => (
                  <li key={q}>
                    <button type="button" onClick={() => ask(q)}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left text-sm text-[#37352F] dark:text-gray-200 hover:bg-[rgba(55,53,47,0.05)]">
                      <span className="flex-1">{q}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-[#9B9A97]" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {messages.map((m) => (
              <AiHelpMessage key={m.id} role={m.role === 'user' ? 'user' : 'assistant'} content={m.content} onInternalLink={openInternal} />
            ))}
            {streaming && (streamingContent
              ? <AiHelpMessage role="assistant" content={streamingContent} streaming onInternalLink={openInternal} />
              : <AiHelpThinking />)}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <AiHelpComposer disabled={streaming} busy={streaming} error={rateLimitError} onSend={ask} />
    </aside>
  );
}
